import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    FormControlLabel,
    Switch,
    TextField,
    Snackbar,
    Alert,
    CircularProgress,
    Divider,
} from '@mui/material';
import {
    Close as CloseIcon,
    Share as ShareIcon,
    Download as DownloadIcon,
    ContentCopy as CopyIcon,
    QrCode as QrCodeIcon,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import FlexCardVisual from './FlexCardVisual';
import { generateQrCodeDataUrl } from '../../utils/qrCode';
import { trackFlexEvent, FLEX_EVENTS } from '../../utils/flexAnalytics';

const ASPECT_RATIOS = ['1:1', '4:5', '9:16', '16:9'];

const SHARE_CAPTIONS = {
    session: (m) => {
        if (m.effectiveWpm) return `Just hit ${m.effectiveWpm} Effective WPM on ReaderFLEX! Speed + comprehension = real reading skill. Think you can beat me?`;
        if (m.wpm && m.comprehension) return `Just read at ${m.wpm} WPM with ${m.comprehension}% comprehension on ReaderFLEX! Think you can beat me?`;
        return `Check out my reading progress on ReaderFLEX!`;
    },
    badge: (m) => `Just unlocked the "${m.badgeName}" badge on ReaderFLEX! ${m.badgeRarity ? `(${m.badgeRarity})` : ''} Can you earn it too?`,
    streak: (m) => `${m.streak}-day reading streak on ReaderFLEX! Consistency is paying off. Can you keep up?`,
    leaderboard: (m) => `Ranked #${m.leaderboardRank} on the ReaderFLEX ${m.leaderboardCategory || 'leaderboard'}! Think you can climb higher?`,
    challenge: (m) => `Challenge ${m.challengeResult || 'complete'} on ReaderFLEX! Ready to test your reading speed?`,
};

export default function FlexCardShareDialog({ open, card, onClose }) {
    const { user } = useAuth();
    const cardRef = useRef();
    const [aspectRatio, setAspectRatio] = useState('1:1');
    const [showName, setShowName] = useState(true);
    const [showQr, setShowQr] = useState(true);
    const [caption, setCaption] = useState('');
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
    const [sharing, setSharing] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [currentCard, setCurrentCard] = useState(card);

    useEffect(() => {
        if (card) {
            setCurrentCard(card);
            setAspectRatio(card.aspectRatio || '1:1');
            const defaultCaption = SHARE_CAPTIONS[card.cardType]?.(card.metrics || {}) || 'Check out my ReaderFLEX achievement!';
            setCaption(defaultCaption);
        }
    }, [card]);

    useEffect(() => {
        if (currentCard?.shareUrl) {
            generateQrCodeDataUrl(currentCard.shareUrl, { width: 200 }).then(setQrCodeDataUrl);
        }
    }, [currentCard?.shareUrl]);

    const displayCard = {
        ...currentCard,
        aspectRatio,
        subheadline: showName ? currentCard?.subheadline : null,
    };

    const handleShare = async () => {
        if (!cardRef.current) return;
        setSharing(true);
        try {
            const canvas = await html2canvas(cardRef.current, {
                backgroundColor: '#0D1421',
                scale: 3,
                useCORS: true,
                allowTaint: false,
                letterRendering: true,
                foreignObjectRendering: false,
            });

            canvas.toBlob(async (blob) => {
                try {
                    const file = new File([blob], 'readerflex-card.png', { type: 'image/png' });
                    const shareData = {
                        title: 'ReaderFLEX Achievement',
                        text: caption,
                    };

                    if (currentCard?.shareUrl) {
                        shareData.url = currentCard.shareUrl;
                    }

                    if (navigator.share && navigator.canShare?.({ files: [file] })) {
                        shareData.files = [file];
                        await navigator.share(shareData);
                        trackShare('native');
                    } else if (navigator.share) {
                        await navigator.share(shareData);
                        trackShare('native');
                    } else {
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = 'readerflex-card.png';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                        trackShare('download');
                        setSnackbar({ open: true, message: 'Card downloaded! Share it wherever you like.', severity: 'success' });
                    }
                } catch (err) {
                    if (err.name !== 'AbortError') {
                        console.error('Share error:', err);
                        setSnackbar({ open: true, message: 'Sharing failed. Try downloading instead.', severity: 'error' });
                    }
                } finally {
                    setSharing(false);
                }
            }, 'image/png');
        } catch (err) {
            console.error('Canvas error:', err);
            setSharing(false);
            setSnackbar({ open: true, message: 'Could not generate card image.', severity: 'error' });
        }
    };

    const handleDownload = async () => {
        if (!cardRef.current) return;
        setDownloading(true);
        try {
            const canvas = await html2canvas(cardRef.current, {
                backgroundColor: '#0D1421',
                scale: 3,
                useCORS: true,
                allowTaint: false,
                letterRendering: true,
                foreignObjectRendering: false,
            });
            canvas.toBlob((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `readerflex-${currentCard?.cardType || 'card'}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                trackShare('download');
                setSnackbar({ open: true, message: 'Card downloaded!', severity: 'success' });
            }, 'image/png');
        } catch (err) {
            console.error('Download error:', err);
            setSnackbar({ open: true, message: 'Download failed.', severity: 'error' });
        } finally {
            setDownloading(false);
        }
    };

    const handleCopyCaption = () => {
        const fullText = `${caption} ${currentCard?.shareUrl || ''}`.trim();
        navigator.clipboard.writeText(fullText);
        setCopied(true);
        trackShare('copy');
        setTimeout(() => setCopied(false), 2500);
        setSnackbar({ open: true, message: 'Caption + link copied to clipboard!', severity: 'success' });
    };

    const trackShare = async (platform) => {
        trackFlexEvent(FLEX_EVENTS.CARD_SHARED, {
            card_id: currentCard?._id || currentCard?.cardId,
            card_type: currentCard?.cardType,
            platform,
            aspect_ratio: aspectRatio,
        });
        try {
            if (currentCard?._id || currentCard?.cardId) {
                await api.post(`/flex/cards/${currentCard._id || currentCard.cardId}/share`, { platform });
            }
        } catch (_) {}
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Share Your FLEX Card
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {/* Card preview */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, overflow: 'auto' }}>
                    <FlexCardVisual
                        ref={cardRef}
                        card={displayCard}
                        qrCodeDataUrl={showQr ? qrCodeDataUrl : null}
                        showQr={showQr}
                    />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Aspect ratio selector */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Card Format</Typography>
                    <ToggleButtonGroup
                        value={aspectRatio}
                        exclusive
                        onChange={(_, v) => v && setAspectRatio(v)}
                        size="small"
                    >
                        {ASPECT_RATIOS.map(r => (
                            <ToggleButton key={r} value={r} sx={{ px: 2, py: 0.5, fontSize: '0.75rem' }}>
                                {r}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                {/* Display controls */}
                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={<Switch checked={showName} onChange={(e) => setShowName(e.target.checked)} size="small" />}
                        label="Show my name"
                    />
                    <FormControlLabel
                        control={<Switch checked={showQr} onChange={(e) => setShowQr(e.target.checked)} size="small" />}
                        label="Show QR code"
                    />
                </Box>

                {/* Caption editor */}
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Share caption"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    size="small"
                    sx={{ mb: 1 }}
                    inputProps={{ maxLength: 280 }}
                />
                <Typography variant="caption" color="text.secondary">
                    {caption.length}/280 characters
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Button
                    onClick={handleCopyCaption}
                    startIcon={<CopyIcon />}
                    variant="outlined"
                    size="small"
                >
                    {copied ? 'Copied!' : 'Copy Caption'}
                </Button>
                <Button
                    onClick={handleDownload}
                    startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                    variant="outlined"
                    size="small"
                    disabled={downloading}
                >
                    Download
                </Button>
                <Button
                    onClick={handleShare}
                    startIcon={sharing ? <CircularProgress size={16} /> : <ShareIcon />}
                    variant="contained"
                    size="small"
                    disabled={sharing}
                >
                    Share
                </Button>
            </DialogActions>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}
